import { useTranslation } from "react-i18next";

import { computationOptions } from "#app/computationOptions";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PriceDisplay } from "#modules/quoteRequestLines/PriceDisplay";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Stack } from "#shared/view/components/Stack";
import { computeAssemblyPrice, isAssemblyOptimized, JobStatusSchema } from "shared";

import { AnalyzeError } from "./AnalyzeError";

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  openDetailsPanel: () => void;
  retryPrediction: () => void;
  condensed?: boolean;
  hasChildren: boolean;
};

export const PartCostDisplay = ({
  quoteRequestLine,
  openDetailsPanel,
  retryPrediction,
  hasChildren,
  condensed = false,
}: Props) => {
  const { t } = useTranslation();

  const { assemblies } = useAssembliesContext();
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine.quoteRequestId);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequestLine.quoteRequestId);

  const currentAssembly = assemblies.find(
    (assembly) => assembly.quoteRequestLine.id === quoteRequestLine.id,
  );

  const {
    assembly: { cost: assemblyCost, price: assemblyPrice },
    parentQuoteRequestLine: { cost: parentQuoteRequestLineCost },
  } = computeAssemblyPrice(
    {
      children: currentAssembly?.children ?? [],
      quoteRequestLine,
      salesMarginRatio: 0,
      optimizedMachineTimes,
      optimizedMaterialCosts,
    },
    computationOptions,
  );

  const isOptimized = isAssemblyOptimized({
    assembly: { quoteRequestLine, children: currentAssembly?.children ?? [] },
    optimizedMachineTimes,
    optimizedMaterialCosts,
  });

  const hasEmptyProcessSteps = quoteRequestLine.processSteps.length === 0;

  if (
    quoteRequestLine.predictionStatus === JobStatusSchema.enum.PROCESSED &&
    hasEmptyProcessSteps &&
    assemblyCost === 0
  ) {
    return (
      <AnalyzeError
        errorText={t("qrl.price.lowConfidence.message")}
        retryText={t("qrl.price.lowConfidence.retryLabel")}
        retryAction={openDetailsPanel}
      />
    );
  }

  if (assemblyCost === 0 && quoteRequestLine.predictionStatus === null) {
    return (
      <AnalyzeError
        errorText={t("qrl.price.noPrediction.message")}
        retryText={t("qrl.price.noPrediction.retryLabel")}
        retryAction={retryPrediction}
      />
    );
  }

  return (
    <Stack gap={0} align={condensed ? "flex-start" : "flex-end"}>
      <PriceDisplay
        primaryCostLabel={
          hasChildren ? t("qrl.price.estimatedAssemblyUnitCost") : t("qrl.price.estimatedCost")
        }
        primaryCost={assemblyCost}
        marginPrice={assemblyPrice}
        secondaryCostLabel={hasChildren ? t("qrl.price.additionalCost") : undefined}
        secondaryCost={hasChildren ? parentQuoteRequestLineCost : undefined}
        disclaimer={false}
        condensed={condensed}
        isOptimized={isOptimized}
      />
    </Stack>
  );
};
